import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DeviceDetectorService, DeviceType } from 'ngx-device-detector';
import { forkJoin, of } from 'rxjs';
import { takeUntil, switchMap, catchError, map } from 'rxjs/operators';
import { boolConverter } from '../../../models/converter/string-bool.converter';
import { Theme } from '../../../models/themes/theme.model';
import { UnsubscribeComponent } from '../../../shared-components/components/unsubscribe/unsubscribe.abstract';
import { MobileApiService } from '../../../shared-services/services/mobile.api.service';
import { SnackBarService } from '../../../shared-services/services/snack-bar.service';
import { ThemeService } from '../../../shared-services/services/theme.service';
import { AssetSharingService } from '../../../verification-service/services/asset-sharing.service';
import { AuthService } from '../../services/auth.service';
import { MixPanelService } from 'app/shared-services/services/mixpanel.service';
import { GetUrlRequest } from 'app/auth/models/get-url-request.model';
import { GetUrlResponse } from 'app/auth/models/get-url-response.model';
import { FreshDeskService } from 'app/shared-services/services/freshdesk.service';
import { FreshDeskSsoResponse } from 'app/models/freshdesk/freshdesksso-response.model';
import { CompanyService } from 'app/shared-services/services/company.service';
import { AnonymousCompanyCustomMessage } from 'app/models/anonymous-company-custom-message.model';
import { Dictionary } from 'app/shared-interface/dictionary.interface';
import { MessageResponse } from 'app/auth/models/message-response.model';

@Component({
    selector: 'app-login',
    templateUrl: 'login.component.html',
    styleUrls: ['./login.component.scss'],
    standalone: false
})
export class LoginComponent extends UnsubscribeComponent implements OnInit, OnDestroy {
    public loading = true;

    public freshDeskUrl: string = '';
    public redirectUrl: string = '';
    public mobileRedirectUrl: string = '';

    public b2cURLs: GetUrlResponse;
    public theme: Theme = new Theme();

    constructor(
        private route: ActivatedRoute,
        private authService: AuthService,
        private companyService: CompanyService,
        private deviceService: DeviceDetectorService,
        private freshDeskService: FreshDeskService,
        private mixPanelService: MixPanelService,
        private mobileApiService: MobileApiService,
        private router: Router,
        private themeService: ThemeService
    ) {
        super();
    }

    ngOnInit(): void {
        this.mixPanelService.setUpMixPanel(true);
        this.getAll();
        this.checkIfMobile();
    }

    private getAll(): void {
        const theme$ = this.themeService.hasThemeInCache()
            ? of(this.themeService.getTheme())
            : this.themeService.loadThemeForHost();

        theme$
            .pipe(
                takeUntil(this.unsubscriber),
                switchMap(theme => {
                    this.theme = theme;
                    if (!theme?.companyCode) {
                        return of(null); // No company code – abort chain gracefully
                    }
                    return forkJoin([
                        this.freshDeskService.getFreshDeskSSOUrl(),
                        this.authService.getB2CURLs(new GetUrlRequest({
                            companyCode: theme.companyCode,
                            domain: window.location.host,
                            invitationKey: undefined
                        })),
                        this.companyService.getAnonymousCompanyCustomMessage(new AnonymousCompanyCustomMessage({
                            companyCode: theme.companyCode,
                            messageCodes: ['PRE_AUTH_MESSAGE_WEB']
                        }))
                    ]).pipe(
                        map(([freshdeskResponse, b2cUrls, message]: [FreshDeskSsoResponse, GetUrlResponse, Dictionary<string>]) => ({ freshdeskResponse, b2cUrls, message }))
                    );
                }),
                catchError(() => {
                    this.handleError();

                    return of(null);
                })
            )
            .subscribe(result => {
                if (!result) {
                    this.handleError();

                    return;
                }
                const { freshdeskResponse, b2cUrls, message } = result;

                const downMessage = message['PRE_AUTH_MESSAGE_WEB'];

                if (downMessage) {
                    this.router.navigate(['/auth/message'], { queryParams: { message: downMessage } });

                    return;
                }

                this.freshDeskUrl = freshdeskResponse.supportCenterUrl;
                this.b2cURLs = b2cUrls;
                this.loading = false;
            });
    }

    private handleError(): void {
        const downMessage = new MessageResponse({
            message: 'Our services are currently unavailable. Please try again later.',
            links: [
                { retry: true, title: 'Retry', url: 'https://' + window.location.host + '/pfm/login' }
            ]
        });

        this.router.navigate(['/auth/message'], { queryParams: { message: JSON.stringify(downMessage) } });
        this.loading = false;
    }

    private checkIfMobile(): void {
        this.route.queryParams
            .subscribe(params => {
                this.redirectUrl = params['redirectUrl'];
                this.mobileRedirectUrl = params['mobileRedirectUrl'];
                this.checkAndOpenApp();
            });
    }

    private checkAndOpenApp(): void {
        let shouldUseBrowser = true;
        const isMobileDevice = this.deviceService.getDeviceInfo().deviceType === DeviceType.Mobile || this.deviceService.getDeviceInfo().deviceType === DeviceType.Tablet;
        if (isMobileDevice && this.redirectUrl != null) {
            this.mobileApiService.getURLSchemeForHost()
                .pipe(takeUntil(this.unsubscriber))
                .subscribe((urlScheme) => {
                    if (urlScheme.scheme) {
                        const deepLinkRedirectUrl = this.mobileRedirectUrl ? this.mobileRedirectUrl : this.redirectUrl;
                        const scheme = urlScheme.scheme + '/deeplink?redirectUrl=' + deepLinkRedirectUrl;
                        const fallbackHandler = function () {
                            shouldUseBrowser = true;
                        };
                        const openApp = function (deeplinkUrl: string) {
                            window.location.replace(deeplinkUrl);
                            shouldUseBrowser = false;
                            setTimeout(fallbackHandler, 10000);
                        };
                        openApp(scheme);
                    }
                });
        }
    }
}
